import { SubscriptionCode } from '@/components/code/SubscriptionCode';
import { WhenToApplyQuote } from '@/components/hql/WhenToApplyQuote';
import { MetadataItem } from '@/components/MetadataItem';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { describeSubscription } from '@/lib/subscriptionCode';
import { processHowOftenResultsState } from '@/state';
import { useLoadableAtomValue } from '@/state/loadableHooks';
import { formatDateTime } from '@/util/dates';
import type { ProcessHowOftenResult, TaskType } from '@rock/types';
import type { FC } from 'react';
import { Link } from 'react-router-dom';

/**
 * A task type's cadence: the "How Often?" text from Notion, the subscription an AI parsed it into (the only
 * thing scheduling reads), and what the latest parse said about it.
 */
export const HowOftenCard: FC<{ taskType: TaskType }> = ({ taskType }) => {
    const [results] = useLoadableAtomValue(processHowOftenResultsState(taskType.id), []);
    const latest = results[0];
    return (
        <Card>
            <CardHeader className="border-b-1 pb-3">
                <CardTitle className="text-xl">How Often</CardTitle>
                <div className="section-description">
                    When this task comes up. Only the parsed subscription is scheduled; it is parsed again whenever the
                    "How Often?" text changes.
                </div>
            </CardHeader>
            <CardContent className="space-y-5">
                <MetadataItem label="How Often?" value={<WhenToApplyQuote text={taskType.aiHowOftenInstructions} />} />
                <MetadataItem
                    label="Parsed Subscription"
                    value={
                        taskType.subscription ? (
                            <div className="space-y-2">
                                <SubscriptionCode subscription={taskType.subscription} block />
                                <div className="text-sm text-muted-foreground">
                                    {describeSubscription(taskType.subscription)}
                                </div>
                            </div>
                        ) : (
                            <span className="text-sm text-muted-foreground">Not Set</span>
                        )
                    }
                />
                {latest ? <LatestParse taskType={taskType} result={latest} /> : null}
            </CardContent>
        </Card>
    );
};

const LatestParse: FC<{ taskType: TaskType; result: ProcessHowOftenResult }> = ({ taskType, result }) => (
    <MetadataItem
        label="Latest Parse"
        value={
            <div className="space-y-1.5 text-sm">
                <div className="whitespace-pre-wrap">{result.result.summary}</div>
                {result.result.issues?.map((issue) => (
                    <div key={issue} className="whitespace-pre-wrap text-amber-800">
                        {issue}
                    </div>
                ))}
                <div className="text-xs text-muted-foreground">
                    {formatDateTime(result.createdAt)} ·{' '}
                    <Link
                        to={`/content/considerations/${taskType.considerationTypeId}/tasks/${taskType.id}/ai-results/process-how-often-results/${result.id}`}
                        className="text-sky-500 hover:underline"
                    >
                        View result
                    </Link>
                </div>
            </div>
        }
    />
);
